import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../utils/firebaseConfig';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { AuthContext } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-toastify';

const TraineeDORsPage = () => {
  const { traineeId } = useParams();
  const { currentUser } = useContext(AuthContext);
  const [dors, setDORs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creatorNames, setCreatorNames] = useState({});
  const [traineeName, setTraineeName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchTraineeDORs = async () => {
      try {
        const q = query(
          collection(db, 'dors'),
          where('traineeId', '==', traineeId),
          orderBy('date', 'desc')
        );
        const snapshot = await getDocs(q);
        const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setDORs(items);

        // Fetch creator names
        const creatorIds = [...new Set(items.map(dor => dor.createdBy))];
        const namesMap = {};
        await Promise.all(creatorIds.map(async (id) => {
          const userDoc = await getDoc(doc(db, 'users', id));
          if (userDoc.exists()) {
            namesMap[id] = userDoc.data().name;
          }
        }));
        setCreatorNames(namesMap);

        // Fetch trainee name
        const traineeDoc = await getDoc(doc(db, 'users', traineeId));
        if (traineeDoc.exists()) {
          setTraineeName(traineeDoc.data().name);
        }

        setError(null);
      } catch (error) {
        console.error('Error fetching trainee DORs:', error);
        setError('Failed to fetch DORs. Please try again later.');
        toast.error('Error loading DORs. The required index may still be building.');
      } finally {
        setLoading(false);
      }
    };

    fetchTraineeDORs();
  }, [currentUser, traineeId, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-6 bg-primary-light dark:bg-primary-dark text-text-light dark:text-text-dark">
        <h1 className="text-2xl font-bold mb-4">Error</h1>
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => navigate('/dors')}
          className="mt-4 bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark px-4 py-2 rounded hover:brightness-110 transition-all duration-300"
        >
          Back to DORs Page
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark">
      <h1 className="text-2xl font-bold mb-4">DORs for Probationary Officer: {traineeName || traineeId}</h1>
      {dors.length === 0 ? (
        <p>No DORs found for this trainee.</p>
      ) : (
        dors.map((dor) => (
          <div
            key={dor.id}
            className="mb-4 p-4 bg-primary-light dark:bg-primary-dark rounded shadow"
          >
            <h2 className="text-lg font-semibold">Date: {dor.date}</h2>
            <p>Shift Length: {dor.shiftLength} hours</p>
            <p>Created By: {creatorNames[dor.createdBy] || 'Unknown'}</p>
            <p>Synopsis: {dor.performanceQuestions.map(q => `${q.question}: ${q.result}`).join(', ')}</p>
            <button
              onClick={() => navigate(`/dor/${dor.id}`)}
              className="mt-2 bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark px-4 py-2 rounded hover:brightness-110 transition-all duration-300"
            >
              View Full DOR
            </button>
          </div>
        ))
      )}
      <button
        onClick={() => navigate('/dors')}
        className="mt-4 bg-secondary-light dark:bg-secondary-dark text-text-light dark:text-text-dark px-4 py-2 rounded hover:bg-accent-light hover:text-primary-light dark:hover:bg-accent-dark dark:hover:text-primary-dark transition-colors duration-300"
      >
        Back to DORs Page
      </button>
    </div>
  );
};

export default TraineeDORsPage;